/**
 * Calculate distance between two geographic points using Haversine formula
 * @param {number} lat1 - Latitude of first point in degrees
 * @param {number} lon1 - Longitude of first point in degrees
 * @param {number} lat2 - Latitude of second point in degrees
 * @param {number} lon2 - Longitude of second point in degrees
 * @returns {number} Distance in meters
 */
const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const earthRadius = 6371000; // Earth radius in meters
  const lat1InRadians = (lat1 * Math.PI) / 180;
  const lat2InRadians = (lat2 * Math.PI) / 180;
  const latDifferenceInRadians = ((lat2 - lat1) * Math.PI) / 180;
  const lonDifferenceInRadians = ((lon2 - lon1) * Math.PI) / 180;

  const haversineSquared =
    Math.sin(latDifferenceInRadians / 2) *
      Math.sin(latDifferenceInRadians / 2) +
    Math.cos(lat1InRadians) *
      Math.cos(lat2InRadians) *
      Math.sin(lonDifferenceInRadians / 2) *
      Math.sin(lonDifferenceInRadians / 2);
  const angularDistance =
    2 *
    Math.atan2(Math.sqrt(haversineSquared), Math.sqrt(1 - haversineSquared));

  return earthRadius * angularDistance; // in meters
};

/**
 * Calculate row length using coordinates
 * @param {Array<Array<number>>} coordinates - Array of [lat, lng] coordinate pairs
 * @returns {number} Length in meters
 */
const calculateRowDimensions = (coordinates) => {
  if (!coordinates || coordinates.length < 2) return { length: 0, width: 0 };
  // Get the longer dimension (length) of the rectangular field
  // Assuming coordinates form a roughly rectangular shape
  const latitudes = coordinates.map((coord) => coord[0]);
  const longitudes = coordinates.map((coord) => coord[1]);

  const latDiff = Math.max(...latitudes) - Math.min(...latitudes);
  const lonDiff = Math.max(...longitudes) - Math.min(...longitudes);

  // Convert lat/lon differences to actual distances
  const avgLat = (Math.max(...latitudes) + Math.min(...latitudes)) / 2;
  const latDistance = calculateDistance(avgLat, 0, avgLat + latDiff, 0);
  const lonDistance = calculateDistance(
    Math.min(...latitudes),
    0,
    Math.min(...latitudes),
    lonDiff,
  );

  return latDistance > lonDistance
    ? { length: latDistance, width: lonDistance }
    : { length: lonDistance, width: latDistance };
};

/**
 * Calculate row length using coordinates
 * @param {Array<Array<number>>} coordinates - Array of [lat, lng] coordinate pairs
 * @returns {number} Length in meters
 */
const calculateRowLength = (coordinates) => {
  return parseFloat(
    (calculateRowDimensions(coordinates)?.length || 0).toFixed(2),
  );
};

/**
 * Safely pull a coordinate list for a given bay/row from metadata.
 * Accepts either rowLabel or rowNumber as the row key.
 */
function getCoordinates(siteMetadata, bayId, rowLabel, rowNumber) {
  const root = siteMetadata?.GREENHOUSE_COORDINATES || {};
  const bayKey = String(bayId ?? "");
  const rowKey =
    rowLabel != null
      ? String(rowLabel)
      : rowNumber != null
        ? String(rowNumber)
        : "";

  let coords = root?.[bayKey]?.[rowKey];

  // Some metadata may store "list of list": [ [ [lat,lng], ... ] ].
  // Normalize to a flat list of [lat,lng] pairs.
  if (Array.isArray(coords) && coords.length > 0) {
    const first = coords[0];
    if (Array.isArray(first) && Array.isArray(first[0])) {
      // coords is nested; prefer the first polygon/line that looks like pairs
      const candidate = coords.find(
        (c) =>
          Array.isArray(c) &&
          Array.isArray(c[0]) &&
          typeof c[0][0] === "number",
      );
      if (candidate) coords = candidate;
    }
  }

  return Array.isArray(coords) ? coords : undefined;
}

export const rowLengthService = {
  /**
   * @param {{ data: Array<{bayId: string|number, rowLabel?: string, rowNumber?: number, points: Array}>,
   *           siteMetadata: any }} args
   * @returns {Array} same array with `rowLength` added (number, meters)
   */
  addRowLength({ data, siteMetadata }) {
    const cache = new Map(); // cache per bay|rowKey -> length

    const getLen = (bayId, rowLabel, rowNumber) => {
      const bayKey = String(bayId ?? "");
      const rowKey =
        rowLabel != null
          ? String(rowLabel)
          : rowNumber != null
            ? String(rowNumber)
            : "";
      const k = `${bayKey}|${rowKey}`;
      if (cache.has(k)) return cache.get(k);

      const coords = getCoordinates(siteMetadata, bayKey, rowLabel, rowNumber);
      const len =
        coords && coords.length >= 2
          ? Number(calculateRowLength(coords)) || 0
          : 0;
      cache.set(k, len);
      return len;
    };

    return (data || []).map((row) => ({
      ...row,
      rowLength: getLen(row.bayId, row.rowLabel, row.rowNumber),
    }));
  },
};
